import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTimesheetHistory } from '@/common/hooks/useTimesheetHistory'
import { AdminOverrideBadge } from '@/common/components/admin-override-badge/AdminOverrideBadge'
import * as styles from './TimesheetHistory.css'

function formatPeriod(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}

function statusClassName(status: string) {
  switch (status) {
    case 'submitted':
      return styles.statusSubmitted
    case 'approved':
      return styles.statusApproved
    case 'rejected':
      return styles.statusRejected
    default:
      return styles.statusDraft
  }
}

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
]

export function TimesheetHistory() {
  const [statusFilter, setStatusFilter] = useState('')
  const { data, isLoading, error } = useTimesheetHistory(statusFilter || undefined)

  if (isLoading) {
    return (
      <div className={styles.page}>
        <h1 className={styles.heading}>Timesheet history</h1>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <h1 className={styles.heading}>Timesheet history</h1>
        <div className={styles.errorBox}>Failed to load: {error.message}</div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Timesheet history</h1>

      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>Filter by status:</span>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {!data || data.length === 0 ? (
        <div className={styles.emptyState}>No timesheets found</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Pay Period</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Total Hours</th>
              <th className={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.map((ts) => (
              <tr key={ts.id} className={styles.row}>
                <td className={styles.td}>
                  {formatPeriod(ts.pay_period.start_date, ts.pay_period.end_date)}
                </td>
                <td className={styles.td}>
                  <span className={statusClassName(ts.status)}>{ts.status}</span>
                  {ts.is_admin_override && (
                    <div className={styles.overrideRow}>
                      <AdminOverrideBadge
                        approverName={ts.approved_by_name}
                        overrideAt={ts.override_at}
                      />
                    </div>
                  )}
                </td>
                <td className={styles.td}>{ts.total_hours}h</td>
                <td className={styles.td}>
                  <Link
                    to="/timesheets"
                    search={{ period_start: ts.pay_period.start_date }}
                    className={styles.viewLink}
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
